import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import NoteIcon from '@mui/icons-material/Note';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';
import RichText from '../components/RichText.tsx';
import { Question } from './PracticePage/types.ts';
import QuestionFilters, { FilterState, YearRow, Subject, Category } from './PracticePage/QuestionFilters.tsx';
import { matchesDifficultyBucket } from './PracticePage/difficulty.ts';
import { matchesMarksBucket } from './PracticePage/marks.ts';
import {
  choicesAsFlatStrings,
  getTableChoiceData,
  isMultipleChoiceQuestion,
} from '../utils/questionChoices.ts';
import { McChoiceTableStudent } from '../components/McChoiceTableStudent.tsx';

const asChoiceArray = (choices: any): string[] => choicesAsFlatStrings(choices);

const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const PAGE_SIZE = 10;

type SavedRow = {
  question_id: string;
  created_at: string;
  questions: Question;
  note?: string | null;
};

const SavedQuestionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // initial load
  const [loadingMore, setLoadingMore] = useState(false); // subsequent pages
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<SavedRow[]>([]);
  const [allRows, setAllRows] = useState<SavedRow[]>([]); // All rows before filtering
  const [userId, setUserId] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  // Data for question origin display
  const [years, setYears] = useState<YearRow[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  
  // Filter state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    years: [],
    subjects: [],
    topics: [],
    subtopics: [],
    types: ['mc', 'input'],
    tags: [],
    difficulty: 'easy',
    marks: null,
  });

  // Notes dialog state
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Unsave confirmation dialog state
  const [unsaveDialogOpen, setUnsaveDialogOpen] = useState(false);
  const [questionToUnsave, setQuestionToUnsave] = useState<string | null>(null);
  const [questionToUnsaveHasNote, setQuestionToUnsaveHasNote] = useState(false);

  // Fetch a page of saved questions
  const fetchPage = async (userAuthId: string, reset: boolean) => {
    const offset = reset ? 0 : cursor;
    const from = offset;
    const to = offset + PAGE_SIZE - 1;

    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    // Get student id from students table
    const { data: student } = await supabase
      .from('students')
      .select('id')
      .eq('user_id', userAuthId)
      .maybeSingle();
    
    if (!student) {
      setError('Student record not found.');
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const studentId = student.id;

    const { data, error } = await supabase
      .from('student_saved_questions')
      .select(
        'question_id, created_at, questions!inner(id, prompt, marks, category_id, type, choices, answer, answer_index, worked_solution, difficulty, unit)',
      )
      .eq('student_user_id', studentId)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      setError(error.message);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const batch: SavedRow[] = (data ?? []).map((r: any) => ({
      question_id: r.question_id,
      created_at: r.created_at,
      questions: Array.isArray(r.questions) ? r.questions[0] : r.questions,
      note: null,
    }));

    // Fetch notes for all questions in this batch
    if (batch.length > 0) {
      const questionIds = batch.map((r) => r.question_id);
      const { data: notesData } = await supabase
        .from('student_question_notes')
        .select('question_id, note')
        .eq('student_user_id', studentId)
        .in('question_id', questionIds);

      if (notesData) {
        const notesMap = new Map(
          notesData.map((n: any) => [n.question_id, n.note])
        );
        batch.forEach((row) => {
          row.note = notesMap.get(row.question_id) || null;
        });
      }
    }

    if (reset) {
      setRows(batch);
      setAllRows(batch);
    } else {
      setRows((prev) => [...prev, ...batch]);
      setAllRows((prev) => [...prev, ...batch]);
    }

    const consumed = offset + batch.length;
    setCursor(consumed);
    setHasMore(batch.length === PAGE_SIZE);

    setLoading(false);
    setLoadingMore(false);
  };

  // Load years, subjects, categories for question origin display
  useEffect(() => {
    if (!userId) return;

    (async () => {
      // Get student id from students table
      const { data: student } = await supabase
        .from('students')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (!student) return;

      const studentId = student.id;

      // Load subjects for student, else the whole shared bank
      const { data: ss } = await supabase
        .from('student_subjects')
        .select('subject_id')
        .eq('student_id', studentId);

      const subjectIds = (ss ?? []).map((r) => r.subject_id);

      let subjectQuery = supabase.from('subjects').select('id, name, year_id');
      if (subjectIds.length) subjectQuery = subjectQuery.in('id', subjectIds);
      const { data: subjectData } = await subjectQuery;
      const subjectsRows: Subject[] = ((subjectData ?? []) as any[]).map((r) => ({
        id: r.id,
        name: r.name,
        year_id: r.year_id ?? null,
      }));
      setSubjects(subjectsRows);

      // Load the entire category tree reachable from these subjects (any depth)
      if (subjectsRows.length) {
        const subjIds = subjectsRows.map((s) => s.id);

        const { data: rootTopics, error: rootErr } = await supabase
          .from('categories')
          .select('id, name, parent_category_id, subject_id')
          .in('subject_id', subjIds)
          .is('parent_category_id', null);

        if (rootErr) {
          console.error(rootErr.message);
          setCats([]);
        } else {
          const all: Record<string, Category> = {};
          const queue: string[] = [];

          (rootTopics ?? []).forEach((c: any) => {
            all[c.id] = c as Category;
            queue.push(c.id);
          });

          // BFS down: fetch children repeatedly until no more
          while (queue.length) {
            const batchParentIds = Array.from(new Set(queue.splice(0, 50)));
            const { data: kids, error: kidsErr } = await supabase
              .from('categories')
              .select('id, name, parent_category_id, subject_id')
              .in('parent_category_id', batchParentIds);

            if (kidsErr) {
              console.error(kidsErr.message);
              break;
            }
            const newChildren: string[] = [];
            (kids ?? []).forEach((c: any) => {
              if (!all[c.id]) {
                all[c.id] = c as Category;
                if (c.parent_category_id) newChildren.push(c.id);
              }
            });
            if (newChildren.length) queue.push(...newChildren);
          }

          setCats(Object.values(all));
        }
      } else {
        setCats([]);
      }

      // Years are a shared global bank, not company-scoped
      const { data: yrs } = await supabase
        .from('years')
        .select('id, year')
        .order('year', { ascending: true });
      setYears((yrs ?? []) as YearRow[]);
    })();
  }, [userId]);

  // Initial load: get user and company, then first page
  useEffect(() => {
    (async () => {
      const {
        data: { user },
        error: uErr,
      } = await supabase.auth.getUser();
      if (uErr || !user) {
        setError('Not authenticated.');
        setLoading(false);
        return;
      }

      setUserId(user.id);
      await fetchPage(user.id, true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to build category IDs from filter selections (same logic as PracticePage)
  const buildCategoryIds = React.useCallback((
    topicsSel: Category[],
    subtopicsSel: Category[],
    subjectsSel: Subject[],
    yearsSel: YearRow[],
  ) => {
    if (subtopicsSel.length) return subtopicsSel.map((s) => s.id);

    if (topicsSel.length) {
      const tIds = new Set(topicsSel.map((t) => t.id));
      const result = new Set<string>();

      topicsSel.forEach((t) => result.add(t.id));

      const byParent: Record<string, string[]> = {};
      cats.forEach((c) => {
        if (c.parent_category_id) {
          (byParent[c.parent_category_id] ||= []).push(c.id);
        }
      });

      const queue = Array.from(tIds);
      while (queue.length) {
        const parent = queue.shift()!;
        const kids = byParent[parent] || [];
        for (const kid of kids) {
          if (!result.has(kid)) {
            result.add(kid);
            queue.push(kid);
          }
        }
      }
      return Array.from(result);
    }

    // subjects OR years (subjects take precedence if selected)
    const effectiveSubjects: Subject[] = subjectsSel.length
      ? subjectsSel
      : yearsSel.length
        ? subjects.filter((s) => s.year_id && yearsSel.some((y) => y.id === s.year_id))
        : [];

    if (effectiveSubjects.length) {
      const subjSet = new Set(effectiveSubjects.map((s) => s.id));
      const result = new Set<string>();

      const byParent: Record<string, string[]> = {};
      const rootsForSubject = new Map<string, string[]>();
      cats.forEach((c) => {
        if (c.parent_category_id) {
          (byParent[c.parent_category_id] ||= []).push(c.id);
        } else if (c.subject_id) {
          (rootsForSubject[c.subject_id] ||= []).push(c.id);
        }
      });

      for (const s of subjSet) {
        const roots = rootsForSubject.get(s) || [];
        const queue = [...roots];
        roots.forEach((r) => result.add(r));
        while (queue.length) {
          const p = queue.shift()!;
          const kids = byParent[p] || [];
          for (const k of kids) {
            if (!result.has(k)) {
              result.add(k);
              queue.push(k);
            }
          }
        }
      }
      return Array.from(result);
    }

    // default: all loaded categories (no filter)
    return null;
  }, [cats, subjects]);

  // State for question tags (question_id -> tag_ids)
  const [questionTagsMap, setQuestionTagsMap] = React.useState<Map<string, string[]>>(new Map());

  // Load tags for all saved questions
  useEffect(() => {
    if (!userId || allRows.length === 0) return;

    (async () => {
      const questionIds = allRows.map((r) => r.questions.id);
      const { data: qtRows } = await supabase
        .from('question_tags')
        .select('question_id, tag_id')
        .in('question_id', questionIds);

      if (qtRows) {
        const map = new Map<string, string[]>();
        qtRows.forEach((r: any) => {
          const qid = r.question_id;
          if (!map.has(qid)) map.set(qid, []);
          map.get(qid)!.push(r.tag_id);
        });
        setQuestionTagsMap(map);
      }
    })();
  }, [userId, allRows]);

  // Apply filters to rows
  useEffect(() => {
    if (allRows.length === 0) return;

    let filtered = [...allRows];

    // Filter by question type
    if (filters.types.length === 1) {
      filtered = filtered.filter((r) =>
        filters.types[0] === 'mc'
          ? isMultipleChoiceQuestion(r.questions.type, r.questions.choices)
          : r.questions.type === 'input',
      );
    }

    if (filters.difficulty) {
      filtered = filtered.filter((r) =>
        matchesDifficultyBucket(r.questions.difficulty, filters.difficulty),
      );
    }

    if (filters.marks) {
      filtered = filtered.filter((r) =>
        matchesMarksBucket(r.questions.marks, filters.marks),
      );
    }

    // Filter by category (topics/subtopics/subjects/years)
    const allowedCategoryIds = buildCategoryIds(
      filters.topics,
      filters.subtopics,
      filters.subjects,
      filters.years,
    );
    if (allowedCategoryIds !== null) {
      const allowedSet = new Set(allowedCategoryIds);
      filtered = filtered.filter((r) => allowedSet.has(r.questions.category_id));
    }

    // Filter by tags
    if (filters.tags.length > 0) {
      const allowedTagIds = new Set(filters.tags.map((t) => t.id));
      filtered = filtered.filter((r) => {
        const questionTagIds = questionTagsMap.get(r.questions.id) || [];
        return questionTagIds.some((tid) => allowedTagIds.has(tid));
      });
    }

    setRows(filtered);
    setCursor(0);
    setHasMore(filtered.length > PAGE_SIZE);
  }, [filters, allRows, buildCategoryIds, questionTagsMap]);

  const handleLoadMore = async () => {
    if (!userId || loadingMore || !hasMore) return;
    await fetchPage(userId, false);
  };

  const handleUnsaveClick = (questionId: string) => {
    const row = rows.find((r) => r.question_id === questionId);
    setQuestionToUnsave(questionId);
    setQuestionToUnsaveHasNote(!!row?.note);
    setUnsaveDialogOpen(true);
  };

  const handleUnsaveConfirm = async () => {
    if (!userId || !questionToUnsave) return;
    
    try {
      // Get student id from students table
      const { data: student } = await supabase
        .from('students')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (!student) {
        alert('Student record not found.');
        return;
      }

      const studentId = student.id;

      // Delete the saved question
      const { error: saveError } = await supabase
        .from('student_saved_questions')
        .delete()
        .eq('student_user_id', studentId)
        .eq('question_id', questionToUnsave);
      if (saveError) throw saveError;

      // Also delete the note if it exists
      const { error: noteError } = await supabase
        .from('student_question_notes')
        .delete()
        .eq('student_user_id', studentId)
        .eq('question_id', questionToUnsave);
      
      // Note: We don't throw on noteError because the note might not exist
      // But we log it for debugging
      if (noteError) {
        console.warn('Error deleting note (may not exist):', noteError);
      }

      setRows((prev) => prev.filter((r) => r.question_id !== questionToUnsave));
      setUnsaveDialogOpen(false);
      setQuestionToUnsave(null);
      setQuestionToUnsaveHasNote(false);
    } catch (e) {
      console.error('Error unsaving question:', e);
      alert('Failed to unsave question. Please try again.');
    }
  };

  const handleUnsaveCancel = () => {
    setUnsaveDialogOpen(false);
    setQuestionToUnsave(null);
    setQuestionToUnsaveHasNote(false);
  };

  const handleOpenNotes = (questionId: string) => {
    const row = rows.find((r) => r.question_id === questionId);
    setCurrentQuestionId(questionId);
    setNoteText(row?.note || '');
    setNotesDialogOpen(true);
  };

  const handleCloseNotes = () => {
    setNotesDialogOpen(false);
    setCurrentQuestionId(null);
    setNoteText('');
  };

  const handleSaveNote = async () => {
    if (!userId || !currentQuestionId) return;

    setSavingNote(true);
    try {
      // Get student id from students table
      const { data: student } = await supabase
        .from('students')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (!student) {
        alert('Student record not found.');
        setSavingNote(false);
        return;
      }

      const studentId = student.id;

      const { error } = await supabase
        .from('student_question_notes')
        .upsert(
          {
            student_user_id: studentId,
            question_id: currentQuestionId,
            note: noteText.trim(),
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'student_user_id,question_id',
          }
        );

      if (error) throw error;

      // Update local state
      setRows((prev) =>
        prev.map((r) =>
          r.question_id === currentQuestionId ? { ...r, note: noteText.trim() || null } : r
        )
      );

      handleCloseNotes();
    } catch (e: any) {
      console.error('Error saving note:', e);
      alert(e.message || 'Failed to save note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleApplyFilters = async (newFilters: FilterState) => {
    setFilters(newFilters);
    // Filtering is handled by useEffect
  };

  // Helper functions for question origin (similar to PracticePage)
  const catById = React.useMemo(() => {
    const m: Record<string, Category> = {};
    cats.forEach((c) => (m[c.id] = c));
    return m;
  }, [cats]);

  const subjById = React.useMemo(() => {
    const m: Record<string, Subject> = {};
    subjects.forEach((s) => (m[s.id] = s));
    return m;
  }, [subjects]);

  const yearById = React.useMemo(() => {
    const m: Record<string, YearRow> = {};
    years.forEach((y) => (m[y.id] = y));
    return m;
  }, [years]);

  // Climb up parent_category_id chain until we find a category with subject_id
  const findSubjectForCategory = React.useCallback((startCatId: string) => {
    let walker = catById[startCatId];
    const guard = new Set<string>(); // prevent cycles (just in case)
    while (walker && !walker.subject_id && walker.parent_category_id && !guard.has(walker.id)) {
      guard.add(walker.id);
      walker = catById[walker.parent_category_id];
    }
    const subjectId = walker?.subject_id ?? null;
    return subjectId ? subjById[subjectId] : undefined;
  }, [catById, subjById]);

  const getQuestionOrigin = React.useCallback((q: Question) => {
    const startCat = catById[q.category_id];
    let leaf = startCat?.name ?? 'Unknown';
    let chain: string[] = [];

    let temp = startCat;
    let hops = 0;
    while (temp && hops < 3 && temp.parent_category_id) {
      const parent = catById[temp.parent_category_id];
      if (!parent) break;
      chain.unshift(parent.name);
      temp = parent;
      hops += 1;
    }
    const categoryText = chain.length ? `${chain.join(' › ')} › ${leaf}` : leaf;

    const subj = startCat ? findSubjectForCategory(startCat.id) : undefined;
    const subjectName = subj?.name ?? '—';
    const yr = subj?.year_id ? yearById[subj.year_id] : undefined;
    const yearText = yr ? String(yr.year) : '—';

    return { yearText, subjectName, categoryText };
  }, [catById, findSubjectForCategory, yearById]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton
          onClick={() => navigate('/practice')}
          aria-label="go back"
          sx={{ mr: 0.5 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          Saved questions
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          onClick={() => setFiltersOpen(true)}
          aria-label="open filters"
          size="small"
        >
          <TuneIcon />
        </IconButton>
      </Box>

      {loading && (
        <Stack alignItems="center" spacing={2} sx={{ mt: 4 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Stack>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          You haven’t saved any questions yet. While practising, use the save button to bookmark
          questions you want to revisit.
        </Typography>
      )}

      <Stack spacing={2}>
        {rows.map((row) => {
          const origin = getQuestionOrigin(row.questions);
          const tableMc = getTableChoiceData(row.questions.choices);
          const isMultipleChoice = isMultipleChoiceQuestion(
            row.questions.type,
            row.questions.choices,
          );
          return (
            <Paper key={row.question_id + row.created_at} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                <Chip size="small" label={`Year: ${origin.yearText}`} />
                <Chip size="small" label={`Subject: ${origin.subjectName}`} />
                <Chip size="small" label={`Category: ${origin.categoryText}`} />
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  size="small"
                  label={new Date(row.created_at).toLocaleString()}
                  color="default"
                  variant="outlined"
                />
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  startIcon={<NoteIcon />}
                  variant={row.note ? 'contained' : 'outlined'}
                  onClick={() => handleOpenNotes(row.question_id)}
                  sx={{ minWidth: 100 }}
                >
                  {row.note ? 'Edit Note' : 'Add Note'}
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleUnsaveClick(row.question_id)}
                >
                  Unsave
                </Button>
              </Stack>
            </Stack>
            <Box sx={{ mb: 1.5 }}>
              <RichText html={row.questions.prompt} className="question-body" />
            </Box>

            {/* Answer / options */}
            {isMultipleChoice && (
              <Box sx={{ mt: 1 }}>
                {tableMc ? (
                  <McChoiceTableStudent
                    readOnly
                    columns={tableMc.columns}
                    rows={tableMc.rows}
                    selectedIndex={row.questions.answer_index ?? undefined}
                    onSelect={() => {}}
                    revealAnswers={row.questions.answer_index != null}
                    correctIndex={row.questions.answer_index}
                    renderCell={(html) => <RichText html={html} />}
                  />
                ) : asChoiceArray(row.questions.choices).map((choice, i) => {
                  const isCorrect =
                    row.questions.answer_index != null && i === row.questions.answer_index;
                  return (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 0.5,
                        p: 0.75,
                        borderRadius: 1,
                        bgcolor: isCorrect ? 'success.light' : 'transparent',
                        border: '1px solid',
                        borderColor: isCorrect ? 'success.main' : 'divider',
                      }}
                    >
                      <Box
                        sx={{
                          fontWeight: 600,
                          minWidth: 24,
                        }}
                      >
                        {letters[i] ?? String(i + 1)}.
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <RichText html={choice} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {row.questions.type === 'input' && (row.questions.answer || row.questions.unit) && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Answer
                </Typography>
                <Typography variant="body2">
                  {(row.questions.answer ?? '').trim()}{' '}
                  {(row.questions.unit ?? '').trim()}
                </Typography>
              </Box>
            )}

            {row.questions.worked_solution && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Worked solution
                </Typography>
                <RichText html={row.questions.worked_solution} />
              </Box>
            )}

            {row.note && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Your Note
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {row.note}
                </Typography>
              </Box>
            )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Question ID: {row.questions.id}
                </Typography>
              </Paper>
            );
          })}
          </Stack>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={handleCloseNotes} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Question Note</Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseNotes}
              aria-label="close"
              size="small"
            >
              ×
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            multiline
            rows={8}
            fullWidth
            variant="outlined"
            placeholder="Add your notes about this question..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotes}>Cancel</Button>
          <Button
            onClick={handleSaveNote}
            variant="contained"
            disabled={savingNote}
          >
            {savingNote ? 'Saving...' : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsave Confirmation Dialog */}
      <Dialog open={unsaveDialogOpen} onClose={handleUnsaveCancel}>
        <DialogTitle>Unsave Question?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {questionToUnsaveHasNote
              ? 'Are you sure you want to unsave this question? This will also permanently delete your note for this question.'
              : 'Are you sure you want to unsave this question?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnsaveCancel}>Cancel</Button>
          <Button onClick={handleUnsaveConfirm} variant="contained" color="error">
            Unsave
          </Button>
        </DialogActions>
      </Dialog>

      {!loading && !error && rows.length > 0 && (
        <Box sx={{ mt: 3 }}>
          {hasMore ? (
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No more saved questions.
            </Typography>
          )}
        </Box>
      )}

      {/* Filters Dialog */}
      <QuestionFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
        userId={userId}
        loading={loading}
      />
    </Container>
  );
};

export default SavedQuestionsPage;


